/**
 * 프로젝트 검색 및 상태 필터 컴포넌트
 * - 컴팩트한 한 줄 레이아웃 (검색 + 드롭다운)
 * - 모바일 터치 친화적 UI
 * - 검색어 디바운스 적용
 */

import { useState, useEffect, useCallback, useRef } from "react";
import { PROJECT_STATUS_LABELS, type ProjectStatus } from "@ict/shared/constants/project.client";

interface SearchFilterProps {
  /** 검색어 변경 핸들러 */
  onSearchChange: (search: string) => void;
  /** 상태 필터 변경 핸들러 */
  onStatusChange: (status: ProjectStatus | null) => void;
  /** 현재 선택된 상태 */
  selectedStatus: ProjectStatus | null;
  /** 디바운스 딜레이 (ms) */
  debounceDelay?: number;
}

// 상태 필터 옵션 + 색상 정의
const STATUS_OPTIONS: {
  value: ProjectStatus | null;
  label: string;
  dotColor: string;
}[] = [
  { value: null, label: "전체", dotColor: "bg-slate-400" },
  { value: "IN_PROGRESS", label: PROJECT_STATUS_LABELS.IN_PROGRESS, dotColor: "bg-emerald-500" },
  { value: "COMPLETED", label: PROJECT_STATUS_LABELS.COMPLETED, dotColor: "bg-slate-500" },
  { value: "PENDING", label: PROJECT_STATUS_LABELS.PENDING, dotColor: "bg-amber-500" },
];

export function SearchFilter({
  onSearchChange,
  onStatusChange,
  selectedStatus,
  debounceDelay = 300,
}: SearchFilterProps) {
  const [searchValue, setSearchValue] = useState("");
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);

  // 현재 선택된 옵션 찾기
  const selectedOption =
    STATUS_OPTIONS.find((opt) => opt.value === selectedStatus) || STATUS_OPTIONS[0];

  // 디바운스된 검색어 변경
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);

      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        onSearchChange(value);
      }, debounceDelay);
    },
    [onSearchChange, debounceDelay]
  );

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // 검색어 초기화 (디바운스 타이머도 정리)
  const handleClearSearch = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    setSearchValue("");
    onSearchChange("");
  }, [onSearchChange]);

  // 상태 선택
  const handleSelectOption = useCallback(
    (value: ProjectStatus | null) => {
      onStatusChange(value);
      setIsSelectOpen(false);
    },
    [onStatusChange]
  );

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(e.target as Node)) {
        setIsSelectOpen(false);
      }
    };

    if (isSelectOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchstart", handleClickOutside as unknown as EventListener);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside as unknown as EventListener);
    };
  }, [isSelectOpen]);

  return (
    <div>
      {/* 한 줄 레이아웃: 검색창 + 상태 드롭다운 */}
      <div className="flex items-center gap-2">
        {/* 검색 입력 */}
        <div className="relative flex-1">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            <svg
              className="h-4 w-4 text-slate-400"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <input
            type="text"
            value={searchValue}
            onChange={handleSearchChange}
            placeholder="프로젝트 검색"
            className="h-11 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-9 text-sm text-slate-700 shadow-sm transition-all placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
            aria-label="프로젝트명 검색"
          />
          {searchValue && (
            <button
              type="button"
              onClick={handleClearSearch}
              className="absolute inset-y-0 right-0 flex items-center pr-3 active:scale-90"
              aria-label="검색어 지우기"
            >
              <svg
                className="h-4 w-4 text-slate-400"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* 상태 필터 드롭다운 */}
        <div ref={selectRef} className="relative">
          <button
            type="button"
            onClick={() => setIsSelectOpen(!isSelectOpen)}
            className={`flex h-11 items-center gap-2 rounded-lg border bg-white px-3 text-sm font-medium shadow-sm transition-all active:scale-[0.98] ${
              isSelectOpen
                ? "border-blue-500 ring-2 ring-blue-500/20"
                : selectedStatus
                  ? "border-slate-300 text-slate-700"
                  : "border-slate-200 text-slate-500"
            }`}
            aria-expanded={isSelectOpen}
            aria-haspopup="listbox"
            aria-label="상태 필터"
          >
            {/* 상태 표시 도트 */}
            <span className={`h-2 w-2 rounded-full ${selectedOption.dotColor}`} />
            <span className="whitespace-nowrap">{selectedOption.label}</span>
            {/* 화살표 아이콘 */}
            <svg
              className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
                isSelectOpen ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* 드롭다운 메뉴 */}
          {isSelectOpen && (
            <div
              className="absolute right-0 top-full z-20 mt-1 w-36 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
              role="listbox"
              aria-label="상태 옵션"
            >
              {STATUS_OPTIONS.map((option) => {
                const isSelected = selectedStatus === option.value;
                return (
                  <button
                    key={option.value ?? "all"}
                    type="button"
                    onClick={() => handleSelectOption(option.value)}
                    className={`flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm transition-colors active:bg-slate-100 ${
                      isSelected
                        ? "bg-blue-50 font-medium text-blue-700"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                    role="option"
                    aria-selected={isSelected}
                  >
                    <span className={`h-2 w-2 rounded-full ${option.dotColor}`} />
                    <span>{option.label}</span>
                    {isSelected && (
                      <svg
                        className="ml-auto h-4 w-4 text-blue-600"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth={2.5}
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
